import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { formatRoute } from 'react-router-named-routes';
// @material-ui/core components
import userProfileStyles from 'assets/jss/material-dashboard-pro-react/views/userProfileStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import Zoom from '@material-ui/core/Zoom/Zoom';
// material-ui icons
import Publish from '@material-ui/icons/CloudUpload';
import UnPublish from '@material-ui/icons/CloudDownload';
// global variables
import { adStatus } from 'config';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import GridContainer from 'components/UI/Grid/GridContainer';
import Button from 'components/UI/CustomButtons/Button';
import Card from 'components/UI/Card/Card';
// our components
import { AD } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import { LoadingAnimation } from 'components/common/Loading';
import ModalConfirmation from 'components/common/ModalConfirmation';
import WithPermission from 'components/User/WithPermission';

/**
 * @description
 * Stateless component.
 *
 * @param {Object} props - Component props.
 */
class AdActions extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      adTitle: '',
      modalIsOpen: false,
    };
  }

  /**
   * @description
   * Open SweetAlert.
   *
   * @param {string} adTitle - Title of the target item.
   */
  handleOpenAlert = adTitle => {
    this.setState({ modalIsOpen: true, adTitle });
  };

  /**
   * @description
   * SweetAlert callback to close alert if user close it manually.
   */
  handleCloseAlert = () => {
    this.setState({ modalIsOpen: false, adTitle: '' });
  };

  /**
   * @description
   * Redirect to ads/update route.
   */
  handleUpdate = () => {
    const { data } = this.props;
    const history = this.context;
    history.push(formatRoute(AD.UPDATE, { id: data.ad.id }));
  };

  render() {
    const { modalIsOpen, adTitle } = this.state;
    const {
      data,
      classes,
      t,
      adActionLoading,
      adReview,
      adUnReview,
      adPublish,
      adUnPublish,
    } = this.props;
    return (
      <React.Fragment>
        <ModalConfirmation
          show={modalIsOpen}
          confirm={() => this.handleSubmit()}
          cancel={() => this.handleCloseAlert()}
          title={<span>{t('common.delete.ad')}</span>}
          description={adTitle}
          confirmLabel={t('common.delete.ad_confirm')}
        />
        <GridContainer>
          <GridItem xs={12}>
            <Card>
              {adActionLoading && (
                <div className={classes.actionsLoader}>
                  <LoadingAnimation />
                </div>
              )}
              <div className={classes.permissionsContainer}>
                <WithPermission allowPermissions={['review_ad']}>
                  {data.ad.reviewed ? (
                    <Button
                      data-cy="adUnReviewButton"
                      default
                      id="ads-unreview"
                      onClick={() => adUnReview([data.ad.id])}
                    >
                      {t('common.unreview')}
                    </Button>
                  ) : (
                    <Button
                      data-cy="adReviewButton"
                      color="primary"
                      id="ads-review"
                      onClick={() => adReview([data.ad.id])}
                    >
                      {t('common.review')}
                    </Button>
                  )}
                </WithPermission>
                {(data.ad.status === adStatus.PUBLISHED_BY_MODERATOR ||
                  data.ad.status === adStatus.PUBLISHED_BY_PROCESSOR ||
                  data.ad.status === adStatus.TO_REVIEW) && (
                  <WithPermission allowPermissions={['publish_ad']}>
                    <Tooltip
                      placement="top"
                      TransitionComponent={Zoom}
                      title={
                        <React.Fragment>
                          {t('common.unpublish')}
                          <span className={classes.arrow} />
                        </React.Fragment>
                      }
                      classes={{
                        tooltip: classes.bootstrapTooltip,
                        tooltipPlacementTop: classes.bootstrapPlacementTop,
                      }}
                    >
                      <Button
                        data-cy="adUnpublishButton"
                        simple
                        color="danger"
                        id="ads-unpublish"
                        onClick={() => adUnPublish([data.ad.id])}
                        className={classes.actionsBtnLabel}
                      >
                        <UnPublish />
                        <span>{t('common.unpublish')}</span>
                      </Button>
                    </Tooltip>
                  </WithPermission>
                )}
                {(data.ad.status === adStatus.UNPUBLISHED_BY_MODERATOR ||
                  data.ad.status === adStatus.UNPUBLISHED_BY_PROCESSOR ||
                  data.ad.status === adStatus.TO_REVIEW) && (
                  <WithPermission allowPermissions={['publish_ad']}>
                    <Tooltip
                      placement="top"
                      TransitionComponent={Zoom}
                      title={
                        <React.Fragment>
                          {t('common.publish')}
                          <span className={classes.arrow} />
                        </React.Fragment>
                      }
                      classes={{
                        tooltip: classes.bootstrapTooltip,
                        tooltipPlacementTop: classes.bootstrapPlacementTop,
                      }}
                    >
                      <Button
                        data-cy="adPublishButton"
                        simple
                        color="success"
                        id="ads-publish"
                        onClick={() => adPublish([data.ad.id])}
                        className={classes.actionsBtnLabel}
                      >
                        <Publish />
                        <span className={classes.actionsBtnLabel}>
                          {t('common.publish')}
                        </span>
                      </Button>
                    </Tooltip>
                  </WithPermission>
                )}
              </div>
            </Card>
          </GridItem>
        </GridContainer>
      </React.Fragment>
    );
  }
}

/**
 * AdActions PropTypes
 * @param classes - prop styles received from withStyles
 * @param data - prop user info received from parents
 * @param t - prop from HOC with string translations
 * @callback adReview - callback to review elements
 * @callback adUnReview - callback to unreview elements
 * @callback adPublish - callback to publish elements
 * @callback adUnPublish - callback to unpublish elements
 * @callback adActionLoading - boolean to show loading when user taps on action
 */

AdActions.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
  adReview: PropTypes.func.isRequired,
  adUnReview: PropTypes.func.isRequired,
  adPublish: PropTypes.func.isRequired,
  adUnPublish: PropTypes.func.isRequired,
  adActionLoading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(withStyles(userProfileStyles)(AdActions));
